/**
 * Submission Triage data table (OR-979) — column-driven.
 *
 * The set of columns comes from `triage-columns.ts` (per view), and the
 * caller passes which column ids are visible (Customize) + the active
 * view (Submissions / Facilities). This component just lays them out:
 * sticky-left pinned columns, the group-label band, sortable headers,
 * and per-id cell rendering. Most cells are "—" placeholders until the
 * list endpoint returns the data (🔧 backend).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Link01, Loading02 } from "@untitledui/icons";
import { Avatar, Badge, Select, Table, Tooltip, cx } from "@tricura/atlas-ui";
import { patchSubmission } from "../../api";
import type { TriageRow } from "../../api";
import { Skeleton } from "../../components/skeleton";
// Scoped out of MVP per Andressa (jun-17): the status-change reason modal
// is not in scope for Submission Triage. Kept here (commented) so it's a
// one-line revert when it returns to scope.
// import { StatusReasonModal } from "../../components/status-reason-modal";
import { StickyColDivider } from "../../components/sticky-col-divider";
import { PlainHeader, SortHeader } from "../../components/table-sort-header";
import { CommentCell } from "../../components/comments/comment-cell";
import type { SubmissionStatus, SubmissionType, UserDisplay } from "../../types/submission";
import { formatSubmissionId } from "../../utils/submission-id";
import {
    type ColumnDef,
    GROUP_LABELS,
    GROUP_ORDER,
    type TriageView,
    columnsForView,
    defaultVisibleIds,
} from "./triage-columns";
import { TRIAGE_COMMENT_CONTROL_COLUMNS, TRIAGE_COMMENT_FIELD } from "./triage-comment-fields";
import type { TriageComments } from "./use-triage-comments";

// ───────────────────────────── Status cell ─────────────────────────────

// Only the statuses the backend v2 enum accepts (see SubmissionStatus in
// types/submission.ts). Ordered along the UW pipeline. The design's wider
// set is a backend dependency — flagged, not offered here.
const STATUSES: { id: SubmissionStatus; label: string }[] = [
    { id: "Draft", label: "Draft" },
    { id: "Awaiting BOR", label: "Awaiting BOR" },
    { id: "Underwriting", label: "Underwriting" },
    { id: "Indications Sent", label: "Indications Sent" },
    { id: "Quote Sent", label: "Quote Sent" },
    { id: "Bound", label: "Bound" },
    { id: "Risk Denied", label: "Risk Denied" },
    { id: "Risk Closed by Agent", label: "Risk Closed by Agent" },
];

// Scoped out of MVP per Andressa (jun-17). When the reason flow returns:
// const REASON_REQUIRED = new Set<SubmissionStatus>(["Risk Denied", "Risk Closed by Agent"]);

function StatusCell({ id, status }: { id: string; status: SubmissionStatus | null }) {
    const [optimistic, setOptimistic] = useState<SubmissionStatus | null>(null);
    const [pending, setPending] = useState(false);
    // Scoped out of MVP per Andressa (jun-17):
    // const [reasonFor, setReasonFor] = useState<SubmissionStatus | null>(null);

    const applyStatus = async (next: SubmissionStatus) => {
        setOptimistic(next);
        setPending(true);
        try {
            await patchSubmission(id, { status: next });
        } catch (err) {
            console.error("Failed to update status", err);
            setOptimistic(null);
        } finally {
            setPending(false);
        }
    };

    // Compare against the currently shown value (optimistic wins over the
    // prop, which doesn't refetch after a save) — otherwise re-selecting the
    // original status reads as "no change" and silently skips the PATCH.
    const value = optimistic ?? status;

    const handleChange = (next: SubmissionStatus) => {
        if (next === value) return;
        // Scoped out of MVP per Andressa (jun-17): no reason modal — apply
        // the status change directly. Re-enable when the flow returns:
        //   if (REASON_REQUIRED.has(next)) { setReasonFor(next); return; }
        void applyStatus(next);
    };

    return (
        <div onClick={(e) => e.stopPropagation()} className="flex w-full min-w-0 items-center gap-2">
            {/* No colored dot — the latest Figma dropped it from the Status
              * select (Gustavo, jun-17). Plain text + chevron. */}
            <Tooltip title={value ?? ""} isDisabled={!value || pending}>
                <Select
                    size="sm"
                    aria-label="Status"
                    placeholder="—"
                    selectedKey={value}
                    items={STATUSES}
                    isDisabled={pending}
                    // Fit the cell (including the saving indicator) so the
                    // selected label truncates. Keep menu options full-width.
                    className="min-w-0 flex-1"
                    popoverClassName="min-w-max"
                    onSelectionChange={(key) => {
                        if (key === null) return;
                        handleChange(key as SubmissionStatus);
                    }}
                >
                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                </Select>
            </Tooltip>
            {pending && <Loading02 aria-label="Saving" aria-hidden="false" className="size-4 text-fg-quaternary shrink-0 animate-spin" />}

            {/* Scoped out of MVP per Andressa (jun-17). Re-enable with the
              * reasonFor state + REASON_REQUIRED branch above:
            <StatusReasonModal
                isOpen={reasonFor !== null}
                status={reasonFor}
                onConfirm={(reason) => {
                    const next = reasonFor;
                    setReasonFor(null);
                    if (next) void applyStatus(next, reason);
                }}
                onCancel={() => setReasonFor(null)}
            /> */}
        </div>
    );
}

// ───────────────────────────── Type badge ─────────────────────────────

type BadgeColor = "success" | "blue" | "warning" | "orange" | "gray";

const TYPE_BADGE: Record<SubmissionType, { label: string; color: BadgeColor }> = {
    "New Business": { label: "New Business", color: "success" },
    Renewal: { label: "Renewal", color: "blue" },
    Endorsement: { label: "Endorsement", color: "warning" },
    Tail: { label: "Tail", color: "orange" },
};

function TypeBadge({ type }: { type: SubmissionType | null }) {
    if (!type) return <Placeholder />;
    const cfg = TYPE_BADGE[type];
    return (
        <Badge color={cfg.color} type="pill-color" size="sm">
            {cfg.label}
        </Badge>
    );
}

function HubspotLink({ hubspotId }: { hubspotId: string | null }) {
    if (!hubspotId) return <Placeholder />;
    return (
        <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className="border-primary bg-primary text-primary shadow-xs hover:bg-secondary inline-flex h-7 items-center gap-1 rounded-full border px-3 text-xs font-semibold transition duration-100 ease-linear"
        >
            Deal
            <Link01 aria-hidden="true" className="size-3 text-fg-quaternary stroke-[2.5px]" />
        </button>
    );
}

function Placeholder() {
    return <span className="text-tertiary text-sm">—</span>;
}

function formatDate(iso: string | null): string {
    if (!iso) return "—";
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return iso;
    return `${m[2]}/${m[3]}/${m[1]}`;
}

function toNum(v: string | number | null): number | null {
    if (v === null) return null;
    const n = typeof v === "string" ? Number(v) : v;
    return Number.isFinite(n) ? n : null;
}

const COMPACT_USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", minimumFractionDigits: 0, maximumFractionDigits: 1 });
const FULL_USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Big premium-like amounts: "$310K", "$1.2M". */
function moneyCompact(v: string | number | null) {
    const n = toNum(v);
    return n === null ? <Placeholder /> : <span className="text-secondary text-sm tabular-nums">{COMPACT_USD.format(n)}</span>;
}

/** Exact fee amounts: "$5,000.00". */
function moneyFull(v: string | number | null) {
    const n = toNum(v);
    return n === null ? <Placeholder /> : <span className="text-secondary text-sm tabular-nums">{FULL_USD.format(n)}</span>;
}

/** Rate stored as a fraction (0.10) → "10.0%". */
function ratePercent(v: string | number | null) {
    const n = toNum(v);
    return n === null ? <Placeholder /> : <span className="text-secondary text-sm tabular-nums">{(n * 100).toFixed(1)}%</span>;
}

/** Deviation from unity (fraction) → signed, colored "+8.8%" / "-5.2%". */
function DeviationCell({ value }: { value: string | number | null }) {
    const n = toNum(value);
    if (n === null) return <Placeholder />;
    const pct = n * 100;
    const sign = pct > 0 ? "+" : "";
    const color = pct > 0 ? "text-success-primary" : pct < 0 ? "text-error-primary" : "text-secondary";
    return <span className={cx("text-sm font-medium tabular-nums", color)}>{`${sign}${pct.toFixed(1)}%`}</span>;
}

function NumberCell({ value }: { value: number | null }) {
    return value === null ? <Placeholder /> : <span className="text-secondary text-sm tabular-nums">{value}</span>;
}

function TextCell({ value, className }: { value: string | null; className?: string }) {
    if (!value) return <Placeholder />;
    return (
        <span className={cx("text-secondary block truncate text-sm", className)} title={value}>
            {value}
        </span>
    );
}

function fullName(user: UserDisplay): string {
    return [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email || "—";
}

function initialsOf(user: UserDisplay): string {
    const fi = user.first_name?.[0] ?? "";
    const li = user.last_name?.[0] ?? "";
    const combined = `${fi}${li}`.toUpperCase();
    return combined || user.email?.[0]?.toUpperCase() || "?";
}

/** Avatar + name for the UW / Approval UW columns. */
function PersonCell({ user }: { user: UserDisplay | null }) {
    if (!user) return <Placeholder />;
    return (
        <span className="inline-flex min-w-0 items-center gap-2">
            <Avatar size="xs" initials={initialsOf(user)} alt={fullName(user)} />
            <span className="text-secondary truncate text-sm">{fullName(user)}</span>
        </span>
    );
}

/** Per-column-id cell renderer, reading the flat TriageRow. Columns
 *  without a case are 🔧 backend placeholders ("—"). */
function renderCell(columnId: string, row: TriageRow) {
    switch (columnId) {
        // ── shared / UW ──
        case "submission_id":
            return <span className="text-primary text-sm font-medium tabular-nums whitespace-nowrap">{formatSubmissionId(row.submissionId)}</span>;
        case "title":
            return <TextCell value={row.title} className="text-primary" />;
        case "facility_name":
            return <TextCell value={row.facilityName} className="text-primary" />;
        case "ccn":
            return <TextCell value={row.ccn} />;
        case "renewal_date":
            return <span className="text-tertiary text-sm tabular-nums">{formatDate(row.renewalDate)}</span>;
        case "status":
        case "quoted_status":
            return <StatusCell id={row.submissionId} status={row.status} />;
        case "type":
            return <TypeBadge type={row.type} />;
        case "submission_date":
            return <span className="text-tertiary text-sm tabular-nums">{formatDate(row.submissionDate)}</span>;
        case "brokerage":
            return <TextCell value={row.brokerage} />;
        case "broker_name":
            return <TextCell value={row.brokerName} />;
        case "expiring_premium":
            return moneyCompact(row.expiringPremium);
        case "expiring_deductible":
            return moneyCompact(row.expiringDeductible);
        case "hubspot":
            return <HubspotLink hubspotId={row.hubspotId} />;
        // ── Risk Management Contact ──
        case "rm_name":
            return <TextCell value={row.rmName} />;
        case "rm_phone":
            return <TextCell value={row.rmPhone} />;
        case "rm_email":
            return <TextCell value={row.rmEmail} />;
        // ── Schedule ──
        case "facility_count":
            return <NumberCell value={row.numFacilities} />;
        case "skilled":
            return <NumberCell value={row.numSkilledBeds} />;
        case "assisted":
            return <NumberCell value={row.numAssistedBeds} />;
        case "independent":
            return <NumberCell value={row.numIndependentBeds} />;
        case "additional_exposure":
            return <NumberCell value={row.numAdditionalBeds} />;
        case "retro_date":
        case "pl_retro_date":
            return <span className="text-tertiary text-sm tabular-nums">{formatDate(row.retroDate)}</span>;
        case "gl_retro_date":
            return <span className="text-tertiary text-sm tabular-nums">{formatDate(row.glRetroDate)}</span>;
        case "facility_address":
            return <TextCell value={row.facilityAddress} />;
        case "facility_city":
            return <TextCell value={row.facilityCity} />;
        case "facility_state":
            return <TextCell value={row.facilityState} />;
        case "facility_county":
            return <TextCell value={row.facilityCounty} />;
        case "facility_zip":
            return <TextCell value={row.facilityZip} />;
        case "facility_geo":
            return <TextCell value={row.facilityGeo} />;
        case "total_licensed_beds":
            return <NumberCell value={row.licensedBeds} />;
        case "occupied_beds":
            return <NumberCell value={row.occupiedBeds} />;
        case "occupancy":
            return ratePercent(row.occupancyRatio);
        // ── Premium Selection ──
        case "quoted_deductible":
            return moneyCompact(row.declaredSir);
        case "selected_premium":
            return moneyCompact(row.selectedPremium);
        case "tria":
            return moneyCompact(row.triaFeeAmount);
        case "gemini_fees":
            return moneyFull(row.techRmFee);
        case "retail_commission":
            return ratePercent(row.agentCommissionRate);
        case "tricura_fees":
            return ratePercent(row.tricuraCommissionRate);
        case "deviation":
            return <DeviationCell value={row.deviationFromUnity} />;
        case "probability":
            return <TextCell value={row.probabilityToBind} />;
        case "uw":
            return <PersonCell user={row.assignee} />;
        case "approval_uw":
            return <PersonCell user={row.approver} />;
        case "policy_number":
            return <TextCell value={row.policyNumber} />;
        default:
            return <Placeholder />;
    }
}

// ───────────────────────────── Group label band ─────────────────────────────

function GroupLabel({ width, children }: { width: number; children: React.ReactNode }) {
    return (
        <div
            // All group bands: left-aligned, bold, primary text, no vertical
            // separators (Figma). Clip the label to its band so a narrow
            // group (few visible columns) doesn't overlap the next band.
            className="text-primary flex items-center justify-start overflow-hidden py-2.5 pl-6 text-sm font-semibold"
            style={{ width }}
        >
            <span className="truncate">{children}</span>
        </div>
    );
}

// Mobile (`<md`): drop the horizontal pinning so the table scrolls as
// one block — the left-pinned columns blanket a narrow viewport
// (OR-1280). Desktop (`md+`) keeps the freeze. The vertical header
// pinning (`sticky top-*` below) stays at all sizes. Matches Staffing Ratios.
// `max-md:static!` drops the inline `left` freeze-offset on mobile (cells are
// position:relative, so the offset would otherwise shift them and leave a gap);
// columns just flow + scroll horizontally below md.
const STICKY_LEFT = "md:sticky md:z-10 max-md:static!";
// ───────────────────────────── Main table ─────────────────────────────

interface SubmissionsTableProps {
    rows: TriageRow[];
    loading?: boolean;
    skeletonRows?: number;
    sort?: string;
    onSortChange?: (next: string | undefined) => void;
    /** Active view — picks the column set. */
    view?: TriageView;
    /** Visible column ids (Customize). Defaults to the view's default set. */
    visibleColumnIds?: string[];
    /** Column order ids (Customize drag-to-reorder). Defaults to natural order. */
    columnOrder?: string[];
    /** Cell comments (OR-1534). Omit to render the table without them.
     *  Submission view only — the backend has no facility-row anchor. */
    comments?: TriageComments;
}

export function SubmissionsTable({
    rows,
    loading = false,
    skeletonRows = 6,
    sort,
    onSortChange,
    view = "submissions",
    visibleColumnIds,
    columnOrder,
    comments,
}: SubmissionsTableProps) {
    const navigate = useNavigate();
    const handleSort = onSortChange ?? (() => undefined);

    // Facility rows are keyed by location id (many per submission), so map
    // each row key back to its submission id for navigation.
    const submissionIdByRowKey = useMemo(() => new Map(rows.map((r) => [r.rowKey, r.submissionId])), [rows]);

    // Resolve the visible columns for this view, honoring the Customize
    // drag-to-reorder order. Sticky columns stay pinned first; otherwise
    // columns sort by group (GROUP_ORDER keeps the bands intact) then by
    // the user's order within the group.
    const columns = useMemo<ColumnDef[]>(() => {
        const all = columnsForView(view);
        const visible = new Set(visibleColumnIds ?? defaultVisibleIds(view));
        const naturalRank = new Map(all.map((c, i) => [c.id, i]));
        const order = columnOrder ?? all.map((c) => c.id);
        const orderRank = new Map(order.map((id, i) => [id, i]));
        const groupRank = (g: ColumnDef["group"]) => GROUP_ORDER.indexOf(g);
        return all
            .filter((c) => visible.has(c.id))
            .sort((a, b) => {
                if (a.sticky !== b.sticky) return a.sticky ? -1 : 1;
                if (a.sticky && b.sticky) return (naturalRank.get(a.id) ?? 0) - (naturalRank.get(b.id) ?? 0);
                const gr = groupRank(a.group) - groupRank(b.group);
                if (gr !== 0) return gr;
                return (orderRank.get(a.id) ?? 0) - (orderRank.get(b.id) ?? 0);
            });
    }, [view, visibleColumnIds, columnOrder]);

    // Cumulative left offset for sticky columns (in order).
    const stickyLefts = useMemo(() => {
        const map = new Map<string, number>();
        let left = 0;
        for (const c of columns) {
            if (c.sticky) {
                map.set(c.id, left);
                left += c.width;
            }
        }
        return map;
    }, [columns]);

    const totalWidth = useMemo(() => columns.reduce((sum, c) => sum + c.width, 0), [columns]);

    // Available width of the scroll wrapper (the table's parent). When it's
    // wider than the columns' declared total, we stretch the columns to fill
    // it — otherwise the table-fixed layout leaves dead space on the right
    // (react-aria's table doesn't auto-distribute the slack like a plain
    // table-fixed does). Tracked via ResizeObserver so it follows the
    // sidebar collapsing, window resizing, etc.
    const tableRef = useRef<HTMLTableElement>(null);
    const [availWidth, setAvailWidth] = useState(0);
    useEffect(() => {
        const wrapper = tableRef.current?.parentElement;
        if (!wrapper) return;
        const ro = new ResizeObserver(([entry]) => {
            if (entry) setAvailWidth(entry.contentRect.width);
        });
        ro.observe(wrapper);
        setAvailWidth(wrapper.clientWidth);
        return () => ro.disconnect();
    }, []);

    // Spread the slack (avail − total) across the NON-sticky columns,
    // proportional to their declared width. Sticky columns keep their fixed
    // width so the pinned-column `left` offsets (computed from those widths)
    // stay exact. Below the total width there's no slack → declared widths
    // (and the table scrolls horizontally as before).
    const widthOf = useMemo(() => {
        const flexBase = columns.filter((c) => !c.sticky).reduce((sum, c) => sum + c.width, 0);
        const slack = Math.max(0, availWidth - totalWidth);
        return (c: ColumnDef): number => {
            if (c.sticky || flexBase === 0 || slack === 0) return c.width;
            return c.width + (slack * c.width) / flexBase;
        };
    }, [columns, availWidth, totalWidth]);

    // Group bands — only groups that have visible columns, in order. Widths
    // mirror the (stretched) column widths so the band stays aligned.
    const bands = useMemo(() => {
        return GROUP_ORDER.map((group) => {
            const cols = columns.filter((c) => c.group === group);
            const width = cols.reduce((sum, c) => sum + widthOf(c), 0);
            return { group, width, count: cols.length };
        }).filter((b) => b.count > 0);
    }, [columns, widthOf]);

    // Id of the right-most pinned column — hosts the divider line.
    const lastStickyId = useMemo(() => {
        let id: string | undefined;
        for (const c of columns) if (c.sticky) id = c.id;
        return id;
    }, [columns]);
    // `md:${bg}` so the opaque background only applies where the column is
    // actually pinned (md+); on mobile the cell inherits the row background.
    const stickyClass = (c: ColumnDef, bg: string) => (c.sticky ? `${STICKY_LEFT} md:${bg}` : undefined);

    const cellStyle = (c: ColumnDef): React.CSSProperties => {
        const w = widthOf(c);
        const base: React.CSSProperties = { width: w, minWidth: w };
        if (c.sticky) base.left = stickyLefts.get(c.id);
        return base;
    };

    const commentFieldOf = (columnId: string) => (comments && view === "submissions" ? TRIAGE_COMMENT_FIELD[columnId] : undefined);

    const renderBodyCell = (c: ColumnDef, row: TriageRow) => {
        const field = commentFieldOf(c.id);
        if (!field || !comments) return renderCell(c.id, row);
        return (
            <CommentCell
                submissionId={row.submissionId}
                page="submission_triage"
                field={field}
                count={comments.countFor(row, field)}
                isControl={TRIAGE_COMMENT_CONTROL_COLUMNS.has(c.id)}
                isOpen={comments.isOpen(row.submissionId, field)}
                onOpenChange={(open) => comments.setOpen(row.submissionId, field, open)}
                onCountChange={(n) => comments.setCount(row.submissionId, field, n)}
                onResolved={(thread) => comments.onResolved(row.submissionId, field, thread)}
            >
                {renderCell(c.id, row)}
            </CommentCell>
        );
    };

    const skeletonItems = Array.from({ length: skeletonRows }, (_, idx) => ({ id: `skeleton-${idx}` }));

    return (
        <Table
            ref={tableRef}
            aria-label="Submissions"
            data-autotest="triage-table-submissions"
            aria-busy={loading || undefined}
            // `table-fixed` makes columns honor their declared widths so the
            // pinned columns don't grow with long titles (which broke the
            // sticky divider + let titles overflow). Cells truncate instead.
            className="table-fixed"
            wrapperClassName="atlas-scrollbar flex-1 min-h-0 overflow-auto"
            topSlot={
                <div className="bg-primary border-secondary sticky top-0 z-30 flex border-b" style={{ minWidth: totalWidth }}>
                    {bands.map((b) => (
                        <GroupLabel key={b.group} width={b.width}>
                            {GROUP_LABELS[b.group]}
                        </GroupLabel>
                    ))}
                </div>
            }
            style={{ minWidth: totalWidth }}
            onRowAction={(key) => {
                if (typeof key === "string" && !key.startsWith("skeleton-")) {
                    const submissionId = submissionIdByRowKey.get(key) ?? key;
                    navigate(`/submissions/${submissionId}/uw-information`);
                }
            }}
        >
            <Table.Header className="bg-secondary sticky top-[36px] z-20">
                {columns.map((c) => (
                    <Table.Head
                        key={c.id}
                        id={c.id}
                        isRowHeader={c.sticky}
                        className={stickyClass(c, "bg-secondary")}
                        style={cellStyle(c)}
                        data-autotest={`triage-table-header-${c.id}`}
                    >
                        {c.sortable ? (
                            <SortHeader field={c.id} sort={sort} onSortChange={handleSort}>
                                {c.label}
                            </SortHeader>
                        ) : (
                            <PlainHeader>{c.label}</PlainHeader>
                        )}
                        {c.id === lastStickyId && <StickyColDivider mdOnly />}
                    </Table.Head>
                ))}
            </Table.Header>

            {loading ? (
                // Same `dependencies` length as the rows body below: react-aria
                // feeds it to a hook, and the two bodies share one slot.
                <Table.Body items={skeletonItems} dependencies={[comments]}>
                    {(item) => (
                        <Table.Row id={item.id}>
                            {columns.map((c) => (
                                <Table.Cell
                                    key={c.id}
                                    className={stickyClass(c, "bg-primary")}
                                    style={cellStyle(c)}
                                >
                                    <Skeleton className="h-4 w-20" />
                                    {c.id === lastStickyId && <StickyColDivider mdOnly />}
                                </Table.Cell>
                            ))}
                        </Table.Row>
                    )}
                </Table.Body>
            ) : (
                // `dependencies`: rows are cached by item, so re-render them
                // when comment counts / the open popover change.
                <Table.Body items={rows} dependencies={[comments]}>
                    {(row) => (
                        <Table.Row id={row.rowKey} data-autotest="triage-table-row" className="cursor-pointer">
                            {columns.map((c) => (
                                <Table.Cell
                                    key={c.id}
                                    className={stickyClass(c, "bg-primary")}
                                    style={cellStyle(c)}
                                    data-autotest={`triage-table-cell-${c.id}`}
                                >
                                    {renderBodyCell(c, row)}
                                    {c.id === lastStickyId && <StickyColDivider mdOnly />}
                                </Table.Cell>
                            ))}
                        </Table.Row>
                    )}
                </Table.Body>
            )}
        </Table>
    );
}
